import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ImageIcon, PauseCircle, Trophy, Sliders, ChevronLeft, ChevronRight,
  RotateCcw, Monitor, ArrowLeft, Loader2, Play, PowerOff,
} from 'lucide-react'
import { imagecompoAPI } from '../lib/api'
import { useImagecompoSocket } from '../lib/imagecompoSocket'
import { Button } from '../components/ui/button'

/* Touch-first regie voor Image Compo — grote knoppen, sticky header met
   huidige stage-view, minimaal tekst. Gebruikt dezelfde updateState/updateShow
   endpoints als de desktop control-pagina; MIDI en Streamdeck blijven parallel
   werken. Portrait op gsm, landscape op tablet. Live-sync via websocket. */

const BASE_VIEWS = [
  { key: 'idle',    label: 'Idle',    icon: PowerOff,   tone: 'bg-slate-700  border-slate-500' },
  { key: 'entry',   label: 'Entry',   icon: ImageIcon,  tone: 'bg-pink-700   border-pink-500' },
  { key: 'pauze',   label: 'Pauze',   icon: PauseCircle,tone: 'bg-amber-700  border-amber-500' },
  { key: 'reveal',  label: 'Reveal',  icon: Trophy,     tone: 'bg-purple-700 border-purple-500' },
]
const PUBLIC_VIEW = { key: 'public_reveal', label: 'Publiek', icon: Trophy, tone: 'bg-cyan-700 border-cyan-500' }
const RESULTS_VIEW = { key: 'results', label: 'Results', icon: Trophy, tone: 'bg-emerald-700 border-emerald-500' }

export default function ImageCompoTouchPage() {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const load = () => imagecompoAPI.getState().then(r => setData(r.data)).catch(() => {})
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  const patch = async (p) => { setBusy(true); try { await imagecompoAPI.updateState(p); await load() } finally { setBusy(false) } }

  if (!data?.show) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-white p-6">
        <div className="text-center">
          <Sliders className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Geen actieve show.</p>
          <Button asChild variant="outline" className="mt-4"><Link to="/imagecompo/admin">Naar admin</Link></Button>
        </div>
      </div>
    )
  }

  const state = data.state || {}
  const show = data.show
  const entries = (data.entries || []).filter(e => e.status === 'approved')
  const publicEnabled = !!show.public_scoring_enabled
  const VIEWS = [...BASE_VIEWS, ...(publicEnabled ? [PUBLIC_VIEW] : []), RESULTS_VIEW]
  const activeView = state.current_view || 'idle'
  const activeMeta = VIEWS.find(v => v.key === activeView) || VIEWS[0]

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Sticky header — huidige state + terug-link */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="text-white hover:bg-white/10">
            <Link to="/imagecompo/control"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/50 truncate">{show.name}</div>
            <div className="text-lg font-bold flex items-center gap-2">
              <activeMeta.icon className="h-5 w-5" />
              {activeMeta.label}
              {busy && <Loader2 className="h-4 w-4 animate-spin text-white/50" />}
            </div>
          </div>
          <Button asChild variant="ghost" size="sm" className="text-white hover:bg-white/10">
            <a href="/imagecompo/stage" target="_blank" rel="noopener"><Monitor className="h-4 w-4" /></a>
          </Button>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-4 max-w-3xl w-full mx-auto">
        {/* View-picker — grote grid */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 px-1">Stage-weergave</div>
          <div className="grid grid-cols-3 gap-2">
            {VIEWS.map(v => {
              const Icon = v.icon
              const on = activeView === v.key
              return (
                <button key={v.key} disabled={busy}
                        onClick={() => patch({ current_view: v.key })}
                        className={`h-24 rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-transform active:scale-95 ${
                          on ? `${v.tone} text-white shadow-lg` : 'border-white/15 bg-white/5 text-white/70'}`}>
                  <Icon className="h-7 w-7" />
                  <span className="text-xs font-semibold uppercase tracking-widest">{v.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Context per view */}
        {activeView === 'entry' && (
          <EntryPicker entries={entries} currentId={state.current_entry_id}
                       patch={patch} busy={busy} />
        )}
        {activeView === 'reveal' && (
          <RevealSteps kind="admin" state={state} data={data} entries={entries}
                       patch={patch} busy={busy} />
        )}
        {activeView === 'public_reveal' && publicEnabled && (
          <RevealSteps kind="public" state={state} data={data} entries={entries}
                       patch={patch} busy={busy} />
        )}

        {/* Blackout-schild — altijd bereikbaar */}
        <button onClick={() => patch({ current_view: 'idle' })} disabled={busy}
                className="w-full h-16 rounded-lg border-2 border-red-500/40 bg-red-500/10 text-red-300 font-bold uppercase tracking-widest active:scale-95 transition-transform">
          Blackout · terug naar idle
        </button>
      </div>
    </div>
  )
}

function EntryPicker({ entries, currentId, patch, busy }) {
  const idx = entries.findIndex(e => e.id === currentId)
  const cur = idx >= 0 ? entries[idx] : null
  const prev = idx > 0 ? entries[idx - 1] : null
  const next = idx >= 0 && idx < entries.length - 1 ? entries[idx + 1] : (idx === -1 ? entries[0] : null)
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-center">
        <div className="text-[10px] uppercase tracking-widest text-white/50">Huidig op stage</div>
        {cur ? (
          <>
            <div className="text-2xl font-bold mt-1 truncate">{cur.title}</div>
            <div className="text-sm text-white/70 truncate">{cur.team_name}</div>
            <div className="text-[10px] text-white/40 mt-1">#{cur.running_order}</div>
          </>
        ) : (
          <div className="text-white/50 italic mt-1">Nog niks geselecteerd</div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button disabled={busy || !prev}
                onClick={() => prev && patch({ current_entry_id: prev.id })}
                className="h-20 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <ChevronLeft className="h-6 w-6" />
          <span className="font-semibold">Vorige</span>
        </button>
        <button disabled={busy || !next}
                onClick={() => next && patch({ current_entry_id: next.id })}
                className="h-20 rounded-lg border-2 border-acid bg-acid/10 text-acid disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <span className="font-semibold">Volgende</span>
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {entries.map(e => (
          <button key={e.id} disabled={busy}
                  onClick={() => patch({ current_entry_id: e.id })}
                  className={`h-14 rounded-lg border-2 text-xs font-semibold uppercase active:scale-95 transition-transform ${
                    e.id === currentId ? 'border-acid bg-acid/20 text-acid' : 'border-white/15 bg-white/5 text-white/70'}`}>
            #{e.running_order}
          </button>
        ))}
      </div>
    </div>
  )
}

function RevealSteps({ kind, state, data, entries, patch, busy }) {
  const isPub = kind === 'public'
  const stepKey = isPub ? 'public_reveal_step' : 'reveal_step'
  const step = state[stepKey] || 0
  const order = (isPub ? data.public_reveal_order : data.reveal_order) || []
  const total = order.length || entries.length
  const nextId = order[step]
  const nextEntry = nextId ? entries.find(e => e.id === nextId) : null
  const reveal = isPub ? data.current_public_reveal : data.current_reveal
  const kleur = isPub ? 'cyan' : 'purple'
  return (
    <div className="space-y-3">
      <div className={`rounded-lg border-2 border-${kleur}-500/50 bg-${kleur}-500/10 p-4`}>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Getoond</div>
            <div className={`text-3xl font-bold text-${kleur}-300 tabular-nums`}>
              {reveal ? (isPub ? reveal.points_public : reveal.points_admin) : '—'}
            </div>
            <div className="text-xs text-white/60 truncate">{reveal?.team_name || 'nog niks'}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Progress</div>
            <div className="text-3xl font-bold tabular-nums">{step}/{total}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Volgende</div>
            <div className="text-2xl font-bold text-pink-400">?</div>
            <div className="text-xs text-white/60 truncate">{nextEntry ? nextEntry.team_name : (step >= total ? 'klaar' : '—')}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <button disabled={busy || step === 0}
                onClick={() => patch({ [stepKey]: 0 })}
                className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center active:scale-95 transition-transform">
          <RotateCcw className="h-5 w-5" />
        </button>
        <button disabled={busy || step === 0}
                onClick={() => patch({ [stepKey]: Math.max(0, step - 1) })}
                className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center active:scale-95 transition-transform">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button disabled={busy || step >= total}
                onClick={() => patch({ [stepKey]: Math.min(total, step + 1) })}
                className={`col-span-2 h-16 rounded-lg border-2 font-bold active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none ${
                  isPub ? 'border-cyan-500 bg-cyan-500/20 text-cyan-300' : 'border-purple-500 bg-purple-500/20 text-purple-300'}`}>
          <ChevronRight className="h-6 w-6 inline mr-1" />
          Reveal
        </button>
      </div>
    </div>
  )
}
